import React from 'react'

import { RHFInput } from 'react-hook-form-input'
import useForm from 'react-hook-form'

import { Box, Paper, Typography } from '@material-ui/core'

import { ButtonGroupForm, Button } from '@yapay/react-components'

import { useSnackbar } from 'hooks'
import {
  CardConfiguration,
  PaymentMethodCheckbox,
  CreditCardCheckbox,
} from './../../components'
import * as service from 'service'
import constants from './../../../../../../constants'
import PaymentMethodsContext from 'contexts/PaymentMethodsContext'
import transfer from 'images/paymentMethodsConfiguration/transfer.png'
import creditCard from 'images/paymentMethodsConfiguration/creditCard.png'

const ConfigurationEdit = () => {
  const snackbar = useSnackbar()
  const {
    register: registerTransfer,
    setValue: setValueTransfer,
    getValues: getValuesTransfer,
    handleSubmit: handleSubmitTransfer,
  } = useForm()
  const {
    register: registerCreditCard,
    setValue: setValueCreditCard,
    getValues: getValuesCreditCard,
    handleSubmit: handleSubmitCreditCard,
  } = useForm()
  const [editTransfer, setEditTransfer] = React.useState(false)
  const [editCreditCard, setEditCreditCard] = React.useState(false)
  const [currentValuesTransfer, setCurrentValuesTransfer] = React.useState(null)
  const [currentValuesCreditCard, setCurrentValuesCreditCard] = React.useState(
    null,
  )
  const [isLoadingTransfer, setIsLoadingTransfer] = React.useState(false)
  const [isLoadingCreditCard, setIsLoadingCreditCard] = React.useState(false)
  const context = React.useContext(PaymentMethodsContext)

  React.useEffect(() => {
    const loadTransfer = async () => {
      try {
        const responseBilletAndTransferPaymentMethods = await service.recurrence.processors.get(
          {
            processor: 'yapay',
            type: 'b',
          },
        )

        constants.paymentMethods.TRANSFERS_CONFIG_GATEWAY.forEach(
          currentPaymentMethod => {
            const active = !!responseBilletAndTransferPaymentMethods.data.paymentMethods.find(
              paymentMethod =>
                paymentMethod.name === currentPaymentMethod.paymentMethod,
            )?.status

            setValueTransfer(currentPaymentMethod.paymentMethod, active)
          },
        )
      } catch (error) {}
    }

    const loadCreditCards = async () => {
      try {
        const responseCardPaymentMethods = await service.recurrence.processors.get(
          {
            processor: 'yapay',
            type: 'c',
          },
        )

        constants.paymentMethods.CREDIT_CARDS_GATEWAY.forEach(
          currentPaymentMethod => {
            const active = !!responseCardPaymentMethods.data.paymentMethods.find(
              paymentMethod =>
                paymentMethod.name === currentPaymentMethod.paymentMethod,
            )?.status

            setValueCreditCard(currentPaymentMethod.paymentMethod, active)
          },
        )
      } catch (error) {}
    }

    loadTransfer()
    loadCreditCards()
  }, [setValueTransfer, setValueCreditCard])

  const handleChangeTransfer = (event, expanded) => {
    setEditTransfer(expanded)

    if (!expanded) {
      Object.keys(currentValuesTransfer).forEach(key =>
        setValueTransfer(key, currentValuesTransfer[key]),
      )
      return
    }

    setCurrentValuesTransfer({ ...getValuesTransfer() })
  }

  const handleChangeCreditCard = (event, expanded) => {
    setEditCreditCard(expanded)

    if (!expanded) {
      Object.keys(currentValuesCreditCard).forEach(key =>
        setValueCreditCard(key, currentValuesCreditCard[key]),
      )
      return
    }

    setCurrentValuesCreditCard({ ...getValuesCreditCard() })
  }

  const handleClickCancelTransfer = () => {
    Object.keys(currentValuesTransfer).forEach(key =>
      setValueTransfer(key, currentValuesTransfer[key]),
    )
    setEditTransfer(false)
  }

  const handleClickCancelCreditCard = () => {
    Object.keys(currentValuesCreditCard).forEach(key =>
      setValueCreditCard(key, currentValuesCreditCard[key]),
    )
    setEditCreditCard(false)
  }

  const updateMethods = React.useCallback(
    async (activeMethods, inactiveMethods) => {
      const responseIntegrationToken = await service.recurrence.settings.get({
        settingId: constants.settings.YAPAY_TOKEN_ACCOUNT,
      })

      const integrationToken = responseIntegrationToken.data.setting.value

      return Promise.all([
        service.recurrence.processors.update({
          processor: 'yapay',
          methods: activeMethods,
          tags: [
            {
              tag: 'token_account',
              value: integrationToken,
            },
          ],
        }),
        ...inactiveMethods.map(method =>
          service.recurrence.processors.inactivate({
            processor: 'yapay',
            method,
          }),
        ),
      ])
    },
    [],
  )

  const onSubmitTransfer = async data => {
    try {
      setIsLoadingTransfer(true)

      const activeTransfers = Object.keys(data).filter(method => data[method])

      const inactiveTransfers = Object.keys(data).filter(
        method => !data[method],
      )

      await updateMethods(activeTransfers, inactiveTransfers)

      snackbar.open({
        message: 'Transferências bancárias alteradas com sucesso!',
        variant: 'success',
      })

      setEditTransfer(false)
      setIsLoadingTransfer(false)
    } catch (error) {
      snackbar.open({
        message:
          'Erro ao alterar as transferências bancárias! Tente novamente!',
        variant: 'error',
      })

      setIsLoadingTransfer(false)
    }
  }

  const onSubmitCreditCard = async data => {
    try {
      setIsLoadingCreditCard(true)

      const activeCreditCards = Object.keys(data).filter(method => data[method])

      const inactiveCreditCards = Object.keys(data).filter(
        method => !data[method],
      )

      await updateMethods(activeCreditCards, inactiveCreditCards)

      snackbar.open({
        message: 'Cartões de crédito alterados com sucesso!',
        variant: 'success',
      })

      setEditCreditCard(false)
      setIsLoadingCreditCard(false)
    } catch (error) {
      snackbar.open({
        message: 'Erro ao alterar os cartões de crédito! Tente novamente!',
        variant: 'error',
      })

      setIsLoadingCreditCard(false)
    }
  }

  return (
    <Box component={Paper} p={{ xs: 2, sm: 4 }} mb={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box mb={{ xs: 0, sm: 2 }}>
          <Typography variant="h5">Configurações</Typography>
        </Box>
      </Box>

      <CardConfiguration
        image={transfer}
        title="Transferência bancária"
        expanded={editTransfer}
        onChange={handleChangeTransfer}
        collapse
      >
        <form onSubmit={handleSubmitTransfer(onSubmitTransfer)}>
          <Box mb={2}>
            <Typography color="textSecondary">
              Para qual(is) banco(s) seu cliente poderá fazer as transferências?
              <br />
              Você pode escolher mais de um.
            </Typography>
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={{
              xs: '1fr 1fr',
              sm: 'repeat(auto-fill, 130px)',
            }}
            gridGap={4}
            mb={4}
          >
            {constants.paymentMethods.TRANSFERS_CONFIG_GATEWAY.map(
              paymentMethod => (
                <RHFInput
                  as={
                    <PaymentMethodCheckbox
                      id={paymentMethod.paymentMethod}
                      image={paymentMethod.image}
                      imageAlt={paymentMethod.name}
                      description={paymentMethod.name.replace(
                        'Transferência ',
                        '',
                      )}
                    />
                  }
                  name={`${paymentMethod.paymentMethod}`}
                  type="checkbox"
                  register={registerTransfer}
                  setValue={setValueTransfer}
                  key={paymentMethod.paymentMethod}
                  disabled={context.error}
                />
              ),
            )}
          </Box>

          <Typography color="textSecondary">
            Observação: as transferências serão creditadas em sua conta Yapay.
          </Typography>

          <Box mt={2}>
            <ButtonGroupForm>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                isLoading={isLoadingTransfer || context.isLoading}
                disabled={
                  isLoadingTransfer || context.isLoading || context.error
                }
              >
                Salvar
              </Button>

              <Button
                variant="outlined"
                color="primary"
                onClick={handleClickCancelTransfer}
              >
                Cancelar
              </Button>
            </ButtonGroupForm>
          </Box>
        </form>
      </CardConfiguration>

      <CardConfiguration
        image={creditCard}
        title="Cartão de crédito"
        expanded={editCreditCard}
        onChange={handleChangeCreditCard}
        collapse
      >
        <form onSubmit={handleSubmitCreditCard(onSubmitCreditCard)}>
          <Box mb={2}>
            <Typography color="textSecondary">
              Selecione as bandeiras que deseja aceitar.
            </Typography>
          </Box>

          <Box
            display="grid"
            gridTemplateColumns={{
              xs: '1fr 1fr',
              md: '1fr 1fr 1fr',
            }}
            gridColumnGap={4}
            gridRowGap={12}
            mb={4}
          >
            {constants.paymentMethods.CREDIT_CARDS_GATEWAY.map(
              paymentMethod => (
                <RHFInput
                  as={
                    <CreditCardCheckbox
                      id={paymentMethod.paymentMethod}
                      image={paymentMethod.image}
                      description={paymentMethod.name}
                    />
                  }
                  name={paymentMethod.paymentMethod}
                  type="checkbox"
                  register={registerCreditCard}
                  setValue={setValueCreditCard}
                  key={paymentMethod.paymentMethod}
                  disabled={context.error}
                />
              ),
            )}
          </Box>

          <Box mt={2}>
            <ButtonGroupForm>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                isLoading={isLoadingCreditCard || context.isLoading}
                disabled={
                  isLoadingCreditCard || context.isLoading || context.error
                }
              >
                Salvar
              </Button>

              <Button
                variant="outlined"
                color="primary"
                onClick={handleClickCancelCreditCard}
              >
                Cancelar
              </Button>
            </ButtonGroupForm>
          </Box>
        </form>
      </CardConfiguration>
    </Box>
  )
}

export default ConfigurationEdit
